import {
  EstateTask_CreateEstateTaskInput,
  EstateTask_FieldsFragment,
  EstateTask_GetAllEstateTasksDocument,
  useEstateTask_UpdateEstateTaskIsArchivedMutation,
  useEstateTask_UpdateEstateTaskMutation,
} from 'queries';

import Modal from 'common/modal';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import EstateTaskForm from './estate-task-form';

type Props = {
  setShowModal: (value: boolean) => void;
  showModal: boolean;
  estateTask: EstateTask_FieldsFragment;
};

const UpdateEstateTaskModal = ({ setShowModal, showModal, estateTask }: Props) => {
  const [updateEstateTask, { loading: updateEstateTaskExecuting }] =
    useEstateTask_UpdateEstateTaskMutation({
      onCompleted: () => {
        showToast({
          message: 'Estate task updated successfully',
          severity: NotificationSeverity.SUCCESS,
        });
        setShowModal(false);
      },
      onError: (e) =>
        showAlert({
          message: `Error updating estate task: ${e.message}`,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const [updateEstateTaskIsArchived, { loading: updateEstateTaskIsArchivedExecuting }] =
    useEstateTask_UpdateEstateTaskIsArchivedMutation({
      refetchQueries: [
        {
          query: EstateTask_GetAllEstateTasksDocument,
          variables: { input: { isArchived: !estateTask.isArchived } },
        },
      ],
      onCompleted: () => {
        showToast({
          message: 'Estate task archived successfully',
          severity: NotificationSeverity.SUCCESS,
        });
        setShowModal(false);
      },
      onError: (e) =>
        showAlert({
          message: `Error archiving estate task: ${e.message}`,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const onSubmit = async (formData: EstateTask_CreateEstateTaskInput) => {
    const {
      estateType,
      group,
      sortOrder,
      label,
      labelHint,
      hasAssignButton,
      assignButtonLabel,
      price,
      allowPaymentQuantity,
      identifier,
      userCompletionRequired,
      userCanAssignAgain,
      deadlineType,
      deadlineMonthsFromDateOfDeath,
      autoDocumentGeneration,
      documentGenerationTemplateId,
      conditionalTemplateQuestionSetId,
      minimumFeatureSubscriptionType,
      isSearch,
      dollarValue,
      shortName,
      icon,
      isFavorite,
    } = formData;

    updateEstateTask({
      variables: {
        input: {
          id: estateTask.id,
          estateType,
          group,
          sortOrder,
          label,
          labelHint,
          hasAssignButton,
          assignButtonLabel,
          price,
          allowPaymentQuantity,
          identifier,
          isSearch,
          dollarValue,
          shortName,
          userCompletionRequired,
          userCanAssignAgain,
          deadlineType,
          deadlineMonthsFromDateOfDeath,
          autoDocumentGeneration,
          documentGenerationTemplateId,
          conditionalTemplateQuestionSetId,
          updateEstateInputDatasInput:
            formData.createEstateInputDatasInput?.map((input) => ({
              // @ts-expect-error - Property 'id' does not exist on type 'EstateInputData_CreateEstateInputDataInput'.
              id: input.id || '',
              estateInputDataFormatId: input.estateInputDataFormatId,
            })) || [],
          minimumFeatureSubscriptionType,
          icon,
          isFavorite,
        },
      },
    });
  };

  const onUpdateIsArchived = async (isArchived: boolean) => {
    updateEstateTaskIsArchived({
      variables: {
        input: {
          id: estateTask.id,
          isArchived,
        },
      },
    });
  };

  const onExitClick = () => {
    setShowModal(false);
  };

  return (
    <Modal
      setShowModal={setShowModal}
      onExitClick={onExitClick}
      showModal={showModal}
      showActions={false}
      content={
        <EstateTaskForm
          onSubmit={onSubmit}
          onUpdateIsArchived={onUpdateIsArchived}
          isEdit
          isSaveDisabled={updateEstateTaskExecuting || updateEstateTaskIsArchivedExecuting}
          values={{
            ...estateTask,
            isArchived: estateTask.isArchived || false,
            createEstateInputDatasInput: estateTask.estateInputDatas || [],
          }}
        />
      }
      loading={false}
      actionDisabled={false}
      maxWidth="sm"
    />
  );
};

export default UpdateEstateTaskModal;
